import { Divider, Drawer, Group, SimpleGrid, Stack } from '@mantine/core';
import type { CeleryQueue } from '@repo/api-codegen';
import { Icon, Text, Title } from '@repo/foundations';

interface QueueDetailDrawerProps {
	queue: CeleryQueue | null;
	onClose: () => void;
}

export function QueueDetailDrawer({ queue, onClose }: QueueDetailDrawerProps) {
	if (!queue) return null;

	const queueInfo = queue;

	return (
		<Drawer
			opened={!!queue}
			onClose={onClose}
			title={
				<Group spacing="xs">
					<Icon name="list" color="icon/primary/default" size="md" />
					<Title size="md" weight="bold" truncate>
						Queue: {queueInfo.name}
					</Title>
				</Group>
			}
			position="right"
			size="xl"
		>
			<Stack spacing="xs">
				{/* Basic Info */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Basic Info
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Queue Name
							</Title>
							<Text size="sm" truncate>
								{queueInfo.name}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Workers
							</Title>
							<Text size="sm" truncate>
								{queueInfo.workers && queueInfo.workers.length > 0
									? queueInfo.workers.join(', ')
									: 'None'}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Routing Key
							</Title>
							<Text size="sm" truncate>
								{queueInfo.routing_key}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Alias
							</Title>
							<Text size="sm" truncate>
								{queueInfo.alias || 'None'}
							</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Queue Properties */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Queue Properties
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Durable
							</Title>
							<Text size="sm">{queueInfo.durable ? 'Yes' : 'No'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Exclusive
							</Title>
							<Text size="sm">{queueInfo.exclusive ? 'Yes' : 'No'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Auto Delete
							</Title>
							<Text size="sm">{queueInfo.auto_delete ? 'Yes' : 'No'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								No Acknowledge
							</Title>
							<Text size="sm">{queueInfo.no_ack ? 'Yes' : 'No'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								No Declare
							</Title>
							<Text size="sm">{queueInfo.no_declare ? 'Yes' : 'No'}</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Exchange Info */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Exchange Info
					</Title>
					<SimpleGrid cols={2}>
						{Object.entries(queueInfo.exchange || {}).map(([key, value]) => (
							<Stack key={key} spacing={0}>
								<Title size="sm" weight="bold">
									{key}
								</Title>
								<Text size="sm" truncate>
									{typeof value === 'object'
										? JSON.stringify(value)
										: String(value)}
								</Text>
							</Stack>
						))}
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Queue Limits */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Queue Limits
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Max Length
							</Title>
							<Text size="sm">{queueInfo.max_length || 'None'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Max Length (Bytes)
							</Title>
							<Text size="sm">{queueInfo.max_length_bytes || 'None'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Max Priority
							</Title>
							<Text size="sm">{queueInfo.max_priority || 'None'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Expires
							</Title>
							<Text size="sm">{queueInfo.expires || 'Never'}</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Message TTL
							</Title>
							<Text size="sm">{queueInfo.message_ttl || 'None'}</Text>
						</Stack>
					</SimpleGrid>
				</Stack>

				<Divider />

				{/* Args */}
				<Stack spacing="xs">
					<Title size="md" weight="bold">
						Args
					</Title>
					<SimpleGrid cols={2}>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Queue Args
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{queueInfo.queue_arguments
									? JSON.stringify(queueInfo.queue_arguments, null, 2)
									: 'None'}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Binding Args
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{queueInfo.binding_arguments
									? JSON.stringify(queueInfo.binding_arguments, null, 2)
									: 'None'}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Consumer Args
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{queueInfo.consumer_arguments
									? JSON.stringify(queueInfo.consumer_arguments, null, 2)
									: 'None'}
							</Text>
						</Stack>
						<Stack spacing={0}>
							<Title size="sm" weight="bold">
								Bindings
							</Title>
							<Text size="sm" style={{ wordBreak: 'break-all' }}>
								{queueInfo.bindings
									? JSON.stringify(queueInfo.bindings, null, 2)
									: 'None'}
							</Text>
						</Stack>
					</SimpleGrid>
				</Stack>
			</Stack>
		</Drawer>
	);
}
